import * as React from 'react';
import { IoIosArrowDown } from 'react-icons/io';
import { IoMdClose } from 'react-icons/io';
import { PrimaryChipWithButton } from '@/components/ui-parts/Chip';
import { cn } from '@/lib/utils';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../custom';
import { Button } from '../default/button';
import { Popover, PopoverContent, PopoverTrigger } from '../default/popover';
import { Checkbox } from './checkbox';

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * マルチセレクトコンポーネントに表示するオプションの配列。
   * 各オプションはラベル、値、およびオプションのアイコンを持ちます。
   */
  options: {
    /** オプションとして表示するテキスト */
    label: string;
    /** オプションに関連付けられた一意の値 */
    value: string;
    /** オプションと共に表示する任意のアイコンコンポーネント */
    icon?: React.ComponentType<{ className?: string }>;
  }[];

  /**
   * 選択された値が変更された時に呼び出されるコールバック関数。
   * 新しく選択された値の配列を受け取ります。
   */
  onValueChange: (value: string[]) => void;

  /** 制御モード時の選択値（react-hook-formのfield.valueなど） */
  value?: string[];

  /** コンポーネントのマウント時のデフォルト選択値 */
  defaultValue?: string[];

  /**
   * 値が選択されていない時に表示されるプレースホルダーテキスト。
   * オプション、デフォルトは "Select options"。
   */
  placeholder?: string;

  /**
   * ポップオーバーのモーダル性。trueの場合、外部要素との相互作用が
   * 無効になり、ポップオーバーコンテンツのみがスクリーンリーダーに表示されます。
   * オプション、デフォルトはfalse。
   */
  modalPopover?: boolean;

  /**
   * trueの場合、マルチセレクトコンポーネントを別のコンポーネントの子として描画します。
   * オプション、デフォルトはfalse。
   */
  asChild?: boolean;

  /**
   * マルチセレクトコンポーネントにカスタムスタイルを適用するための追加のクラス名。
   * オプション、カスタムスタイルの追加に使用可能。
   */
  className?: string;
}

const arraysEqualInOrder = (a: string[], b: string[]) => a.length === b.length && a.every((value, index) => value === b[index]);
const arraysEqualIgnoringOrder = (a: string[], b: string[]) => a.length === b.length && a.every((value) => b.includes(value));
const normalizeSelectedValues = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      value,
      defaultValue = [],
      placeholder = 'Select options',
      modalPopover = false,
      asChild = false,
      className,
      ...props
    },
    ref,
  ) => {
    const isControlled = value !== undefined;
    const controlledValues = normalizeSelectedValues(value);
    const [selectedValues, setSelectedValues] = React.useState<string[]>(() =>
      isControlled ? controlledValues : defaultValue,
    );
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

    // 制御モード: value propの変化に追従
    React.useEffect(() => {
      if (!isControlled) return;
      setSelectedValues((prev) => {
        if (arraysEqualInOrder(prev, controlledValues) || arraysEqualIgnoringOrder(prev, controlledValues)) {
          return prev;
        }
        return [...controlledValues];
      });
    }, [controlledValues, isControlled]);

    // 非制御モード: defaultValueの変化に追従
    React.useEffect(() => {
      if (isControlled) return;
      const nextValue = defaultValue ?? [];
      setSelectedValues((prev) => {
        if (arraysEqualInOrder(prev, nextValue) || arraysEqualIgnoringOrder(prev, nextValue)) {
          return prev;
        }
        return [...nextValue];
      });
    }, [defaultValue, isControlled]);

    const currentSelectedValues = isControlled ? controlledValues : selectedValues;

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...currentSelectedValues];
        newSelectedValues.pop();
        if (!isControlled) setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (option: string) => {
      const newSelectedValues = currentSelectedValues.includes(option)
        ? currentSelectedValues.filter((value) => value !== option)
        : [...currentSelectedValues, option];
      if (!isControlled) setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      if (!isControlled) setSelectedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };

    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            asChild
            onClick={handleTogglePopover}
            className={cn(
              'border-shade-light-default bg-shade-white-default text-shade-dark-default placeholder:text-shade-light-default flex w-full rounded border relative h-fit min-h-11 pl-2 pr-1 py-1',
              className,
            )}
          >
            {currentSelectedValues.length > 0 ? (
              <div className="flex w-full items-center justify-between gap-1">
                <div className="flex flex-wrap items-center gap-1">
                  {currentSelectedValues.map((value) => {
                    const option = options.find((o) => o.value === value);
                    return (
                      <PrimaryChipWithButton
                        key={value}
                        label={option?.label || ''}
                        className="text-[14px] leading-[24px]"
                        onClose={() => toggleOption(value)}
                      />
                    );
                  })}
                </div>
                <div className="flex items-center justify-between gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="bg-shade-dark-disabled flex h-6 w-6 cursor-pointer p-1"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClear();
                    }}
                  >
                    <IoMdClose size={16} className="text-[#B7C9C9]" />
                  </Button>
                  <Button
                    asChild
                    className="bg-shade-white-default border-shade-light-default flex h-6 w-8 items-center justify-center rounded-l-none border-0 border-l px-2 py-0"
                  >
                    <IoIosArrowDown size={24} className="text-secondary-dark-default bg-secondary-dark-default" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex w-full items-center justify-between">
                <span className="text-shade-medium-default text-sm">{placeholder}</span>
                <Button
                  asChild
                  className="bg-shade-white-default border-shade-light-default flex h-6 w-8 items-center justify-center rounded-l-none border-0 border-l px-2 py-0"
                >
                  <IoIosArrowDown size={24} className="text-secondary-dark-default bg-secondary-dark-default" />
                </Button>
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="bg-shade-white-default border-shade-light-default w-auto p-0"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command>
            <CommandInput placeholder="Search..." onKeyDown={handleInputKeyDown} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = currentSelectedValues.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => toggleOption(option.value)}
                      className="cursor-pointer"
                    >
                      <Checkbox checkboxType="square" checked={isSelected} />
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

MultiSelect.displayName = 'MultiSelect';
